import React, { useContext, useEffect, useState } from "react";
import { Spin, Tooltip, Empty, message } from "antd";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../config/AuthContext";
import { getJavamonImage } from "../../../dao/jutge/GetJavamonConfig";
import { GetJutgeUsuariIdJavamons } from "../../../dao/jutge/GetJutgeUsuariIdJavamons";

/**
 * Javamons obtenidos en estilo RPG para el juego.
 * Iframe NO a pantalla completa: cuadrícula de casillas pequeñas con la imagen
 * del javamon; al pasar el ratón se ve su nombre/descripción. Al hacer click se
 * invoca al javamon como mascota al lado del jugador (igual que el menú in-game
 * de Unity, tecla M): se avisa al host (ZeldaUnityGame) por postMessage y este
 * llama a Unity -> LoginManager.SpawnJavamonPetByName(originName).
 */

interface Javamon {
    id: number;
    id_instance?: number;
    nom: string;
    nom_origin?: string;
    descr?: string | null;
    image?: string;
    rarity?: string;
    captura?: string;
}

// Replica el fallback de Unity para obtener el nombre del prefab a spawnear:
// nom_origin -> nombre de archivo de la imagen -> nom.
function resolveOriginName(jm: Javamon): string {
    if (jm.nom_origin && jm.nom_origin.trim()) return jm.nom_origin.trim();
    if (jm.image) {
        const m = jm.image.match(/\/([^/\\.]+)\.[a-zA-Z]+(?:["']|$)/);
        if (m && m[1]) return m[1];
    }
    if (jm.nom && jm.nom.trim()) return jm.nom.trim();
    return "Monster1";
}

const GameJavamons: React.FC = () => {
    const { isLoggedIn, userId } = useContext(AuthContext);
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [javamons, setJavamons] = useState<Javamon[]>([]);

    // Solo accesible dentro del iframe del juego.
    useEffect(() => {
        if (window.self === window.top) {
            navigate("/home", { replace: true });
        }
    }, [navigate]);

    useEffect(() => {
        if (!isLoggedIn || !userId) return;
        (async () => {
            setLoading(true);
            try {
                const jm = await GetJutgeUsuariIdJavamons(userId + "");
                setJavamons(Array.isArray(jm) ? jm : []);
            } catch (e) {
                console.error("Error cargando los javamons:", e);
            } finally {
                setLoading(false);
            }
        })();
    }, [isLoggedIn, userId]);

    const handleSpawn = (jm: Javamon) => {
        const originName = resolveOriginName(jm);
        // Avisar al host para que Unity invoque la mascota al lado del jugador.
        window.parent?.postMessage({ type: "SPAWN_JAVAMON_PET", prefabName: originName }, "*");
        message.success(`¡${jm.nom} te acompaña!`);
    };

    const renderTooltip = (jm: Javamon) => (
        <div style={{ maxWidth: 220 }}>
            <div style={{ fontWeight: 700, marginBottom: 2 }}>{jm.nom}</div>
            {jm.descr && <div style={{ fontSize: 12 }}>{jm.descr}</div>}
            <div style={{ fontSize: 11, color: "#9fe0a0", marginTop: 3 }}>Click para invocarlo a tu lado</div>
        </div>
    );

    return (
        <div
            style={{
                width: "100%",
                height: "100%",
                minHeight: "100%",
                boxSizing: "border-box",
                padding: 16,
                background: "linear-gradient(180deg, #1a2a3b 0%, #12202a 100%)",
                color: "#d6ecf5",
                fontFamily: "'Segoe UI', sans-serif",
                overflowY: "auto",
            }}
        >
            <div style={{ textAlign: "center", marginBottom: 14 }}>
                <span style={{ fontSize: 22, fontWeight: 800, letterSpacing: 1, textShadow: "0 2px 0 rgba(0,0,0,0.4)" }}>
                    🐾 JAVAMONS
                </span>
            </div>

            {loading ? (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: 40 }}>
                    <Spin size="large" />
                </div>
            ) : javamons.length === 0 ? (
                <Empty
                    description={<span style={{ color: "#adc7d8" }}>Aún no has capturado ningún Javamon.</span>}
                    style={{ marginTop: 30 }}
                />
            ) : (
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(72px, 1fr))",
                        gap: 10,
                    }}
                >
                    {javamons.map((jm, idx) => (
                        <Tooltip key={`${jm.id_instance ?? jm.id}-${idx}`} title={renderTooltip(jm)} placement="top">
                            <div
                                onClick={() => handleSpawn(jm)}
                                style={{
                                    aspectRatio: "1 / 1",
                                    borderRadius: 10,
                                    background: "radial-gradient(circle at 50% 35%, #35506b 0%, #24384a 100%)",
                                    border: "2px solid #38627a",
                                    boxShadow: "inset 0 2px 4px rgba(0,0,0,0.4)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    transition: "transform 0.1s, border-color 0.1s",
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = "scale(1.06)";
                                    e.currentTarget.style.borderColor = "#60c0f0";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = "scale(1)";
                                    e.currentTarget.style.borderColor = "#38627a";
                                }}
                            >
                                <img
                                    src={getJavamonImage(jm)}
                                    alt={jm.nom}
                                    style={{ width: "80%", height: "80%", objectFit: "contain", imageRendering: "pixelated" }}
                                    onError={(e) => { (e.target as HTMLImageElement).style.visibility = "hidden"; }}
                                />
                            </div>
                        </Tooltip>
                    ))}
                </div>
            )}
        </div>
    );
};

export default GameJavamons;
